import Logo from '@/components/Logo.js';
import { SnackbarProvider } from '@/context/SnackbarContext.js';
import { useFirebaseAuth } from '@/utils/firebase.js';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { Card } from '@repo/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import EmailVerification from '../pages/auth/signup/EmailVerification.js';
import { useCommonCurrentUser, useCurrentStudentUser } from '../utils/api.js';
import MagicLoading from './MagicLoading.js';
import { initDevTools } from '@/utils/inits.js';

/**
 * Layout for authentication-related pages (login, signup, etc.)
 * Also handles auth routing based on user state and profile completion
 */
const AuthLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: firebaseUser, isInitialized } = useFirebaseAuth()
  const [showEmailVerification, setShowEmailVerification] = useState(false);

  // Base auth paths that are allowed without authentication
  const baseAuthPaths = useMemo(() => [
    '/auth/login',
    '/auth/signup',
  ], []);
  // Use common endpoint for routing decisions
  const { data: commonUser, isLoading: isCommonLoading, isError: isCommonError } = useCommonCurrentUser({
    enabled: isInitialized && !!firebaseUser,
    showErrorSnackbar: false
  });

  // Use student-specific endpoint only when we know user is student
  const { data: user, isLoading: isStudentLoading, isError: isStudentError } = useCurrentStudentUser({
    enabled: isInitialized && !!firebaseUser && commonUser?.data?.userType === 'student',
    showErrorSnackbar: false
  });

  const isLoading = isCommonLoading || (commonUser?.data?.userType === 'student' && isStudentLoading);
  const isError = isCommonError || (commonUser?.data?.userType === 'student' && isStudentError);


  // Helper function to navigate while preserving URL parameters
  const navigateWithParams = useCallback((path: string) => {
    const searchParams = new URLSearchParams(location.search);
    const fullPath = searchParams.toString() ? `${path}?${searchParams.toString()}` : path;
    navigate(fullPath);
  }, [navigate, location.search]);

  // Handle magic link token persistence
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const magicToken = urlParams.get('magic_link_token');
    if (magicToken) {
      localStorage.setItem('magic_link_token', magicToken);
    }
  }, [location.search]);

  useEffect(() => {
    // Only fetch current user when authenticated
    if (!isInitialized) return;
    const currentPath = location.pathname;
    if (!firebaseUser) {
      if (!baseAuthPaths.some(path => currentPath === path)) {
        navigateWithParams('/auth/login');
      }
      return;
    }

    // Check email verification for authenticated users - LOCK THEM IN if not verified
    if (firebaseUser && !firebaseUser.emailVerified) {
      // Show verification dialog for ANY authenticated user with unverified email
      setShowEmailVerification(true);
      return; // Block all other logic until email is verified
    } else {
      setShowEmailVerification(false);
    }

    // Handle user role-based routing
    if (commonUser?.data) {
      if (commonUser.data.cookies?.developerAid) {
        initDevTools();
      }

      const currentUserType = commonUser.data.userType;

      if (currentUserType === 'staff') {
        // User is a staff member trying to access student area, redirect to school app
        navigate('/school/app');
        return;
      }
    }

    if (isLoading) return;
    if (isError && !baseAuthPaths.some(path => currentPath === path)) {
      navigateWithParams('/auth/login');
      return;
    }

    // If authenticated and on a login/signup page, redirect based on profile stage
    if (user) {
      const isRegisterComplete = Boolean(user.data && user.data.studentDetails && user.data.firstName && user.data.dateOfBirth && user.data.studentDetails.educationDetails && user.data.studentDetails.educationDetails.curricula?.length > 0 && user.data.studentDetails.citizenships);
      if (isRegisterComplete) {
        navigate('/app/home');
        return;
      } else {
        navigateWithParams('/auth/signup/details');
      }
    } else {
      // Firebase user exists, but no user yet - redirect to setup page
      navigateWithParams('/auth/signup/details');
    }
  }, [baseAuthPaths, commonUser, firebaseUser, isError, isCommonError, isInitialized, isLoading, location.pathname, navigate, navigateWithParams, user]);

  // Show loading while checking auth (only when we need to)
  if (isLoading) {
    return (
      <MagicLoading
        active
        onMagic={() => navigate('/auth/login')}
      />
    );
  }

  const isBaseAuthPath = baseAuthPaths.some(path => location.pathname.split('?')[0] === path);

  return (
    <SnackbarProvider>
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          minHeight: '100vh',
          backgroundColor: '#f5f5f5',
          py: { xs: 2, md: 4, lg: 8 }
        }}
        spacing={2}
      >
        <Stack flex={1} direction='row' justifyContent='center'>
          <Logo height={72} />
        </Stack>
        <Box sx={{ maxWidth: 'sm', flex: 1, width: '100%' }}>
          <Card
            sx={{
              p: 4,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 2,
            }}
          >
            <Outlet />
            {isBaseAuthPath && <>
              <Divider sx={{ my: 2, width: '100%' }} />
              <Typography color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                <Typography component="span">
                  By creating an account, or logging in, you agree to our {' '}
                </Typography>
                <Typography
                  component="a"
                  href="https://forum.getzube.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ textDecoration: 'underline', display: 'inline' }}
                >
                  Privacy Policy
                </Typography>
                <Typography component="span">
                  , {' '}
                </Typography>
                <Typography
                  component="a"
                  href="https://forum.getzube.com/privacy/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ textDecoration: 'underline', display: 'inline' }}
                >
                  Terms of Service
                </Typography>
                <Typography component="span">
                  , and {' '}
                </Typography>
                <Typography
                  component="a"
                  href="https://forum.getzube.com/privacy/cookies"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ textDecoration: 'underline', display: 'inline' }}
                >
                  Cookie Notice
                </Typography>
                .
              </Typography>
            </>}
          </Card>
        </Box>
        <Box flex={1} />

        {/* Email verification dialog - LOCK USER IN if email not verified */}
        {firebaseUser && (
          <EmailVerification
            email={firebaseUser.email || ''}
            open={showEmailVerification}
          />
        )}
      </Stack>
    </SnackbarProvider>
  );
};

export default AuthLayout; 